import { useState } from "react";
import { X, Repeat, Warning } from "@phosphor-icons/react";

const SCOPE_OPTIONS = [
  {
    value: "this",
    title: "Just this session",
    body: "Only the selected occurrence will be cancelled. The rest of the series stays scheduled.",
  },
  {
    value: "future",
    title: "This and future occurrences",
    body: "Cancels the selected session plus every later occurrence in the same series.",
  },
  {
    value: "all",
    title: "The entire series",
    body: "Cancels every occurrence in this series — past sessions stay logged but future ones are wiped.",
  },
];

/**
 * Modal asking how to apply a cancel/delete to a recurring session.
 *  - If session has no series_id → render nothing (caller should just do single)
 *  - Otherwise: lets the user pick scope (this / future / all) and confirms.
 *
 * onConfirm(scope: "this" | "future" | "all") is called on submit.
 */
export default function SeriesCancelDialog({ session, action = "cancel", onClose, onConfirm }) {
  const [scope, setScope] = useState("this");
  const [busy, setBusy] = useState(false);

  if (!session) return null;

  const isSeries = !!session.series_id;
  const verb = action === "delete" ? "Delete" : "Cancel";
  const verbLower = action === "delete" ? "delete" : "cancel";

  const handle = async () => {
    setBusy(true);
    try {
      await onConfirm(isSeries ? scope : "this");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="series-cancel-dialog">
      <div className="card-flat w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="tracker text-[#FF6B61] mb-2 flex items-center gap-2">
              <Warning size={14} weight="bold" /> [ {verb} session ]
            </div>
            <h2 className="font-display text-2xl">{session.client?.name || "Session"}.</h2>
            <p className="tracker text-zinc-500 text-[10px] mt-1">
              {new Date(session.start_time).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} · {session.session_type}
            </p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="series-cancel-close">
            <X size={18} weight="bold" />
          </button>
        </div>

        {isSeries ? (
          <>
            <div className="flex items-center gap-2 mb-4 text-xs text-zinc-400">
              <Repeat size={14} weight="bold" className="text-brand-light" />
              This session is part of a recurring series.
            </div>
            <div className="space-y-2">
              {SCOPE_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className={`block border p-3 cursor-pointer transition-colors ${
                    scope === opt.value
                      ? "border-brand bg-brand/10"
                      : "border-white/10 hover:border-white/40"
                  }`}
                  data-testid={`series-scope-${opt.value}`}
                >
                  <input
                    type="radio"
                    name="series-scope"
                    value={opt.value}
                    checked={scope === opt.value}
                    onChange={() => setScope(opt.value)}
                    className="sr-only"
                  />
                  <div className="font-display text-sm leading-none mb-1">{opt.title}</div>
                  <div className="tracker text-zinc-500 text-[10px] normal-case">{opt.body}</div>
                </label>
              ))}
            </div>
          </>
        ) : (
          <p className="text-sm text-zinc-400 mb-4">
            Are you sure you want to {verbLower} this session? Any prepaid balance will be refunded
            to the client&apos;s pot.
          </p>
        )}

        <div className="flex items-center gap-3 mt-6">
          <button
            type="button"
            onClick={handle}
            disabled={busy}
            className="btn-primary !bg-[#7a1d1d] hover:!bg-[#a82828] disabled:opacity-50"
            data-testid="series-cancel-confirm"
          >
            {busy ? `${verb.replace(/e$/, "")}ing…` : `${verb} ${isSeries && scope !== "this" ? scope : ""}`.trim()}
          </button>
          <button type="button" onClick={onClose} className="btn-ghost" data-testid="series-cancel-back">
            Back
          </button>
        </div>
      </div>
    </div>
  );
}
